import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { App, Button, Col, Form, Input, Modal, Row, Space, Spin, Tooltip } from 'antd';

import * as sharedActions from '../../../shared/store/actions';
import * as actions from '../store/actions';

import { UserTypes } from '../../../shared/constants/enums';

import { isEmpty } from 'lodash';

import { AppDispatch, RootState } from '../../../store';
import { Store } from 'antd/es/form/interface';
import { SaveGlobalTelecomAccountRequest } from '../models';

export type GlobalTelecomAccountComponentProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const GlobalTelecomAccountComponent = ({ showModal, onCloseModal, resetAndRefresh }: GlobalTelecomAccountComponentProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { modal } = App.useApp();
  const didMount = useRef(false);

  const [formRef] = Form.useForm<SaveGlobalTelecomAccountRequest>();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);
  const [isAgencyAccount, setIsAgencyAccount] = useState<boolean>(false);

  const { globalTelecomAccount, globalTelecomAccountLoading, saveGlobalTelecomAccountLoading } = useSelector(
    (state: RootState) => state.globalTelecom,
  );

  const { messageControlParametersLoading } = useSelector((state: RootState) => state.smses);
  const { userInfo } = useSelector((state: RootState) => state.shared);

  useEffect(() => {
    if (!didMount.current) {
      dispatch(sharedActions.getUserInfo());

      dispatch(actions.getGlobalTelecomAccount({ isAgencyAccount: false })).then(() => {
        setInitiatingDone(true);
        onReset();
      });

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    dispatch(
      actions.getGlobalTelecomAccount({
        isAgencyAccount: isAgencyAccount,
      }),
    ).then(() => {
      onReset();
    });
  }, [isAgencyAccount]);

  useEffect(() => {
    onReset();
  }, [globalTelecomAccount, globalTelecomAccountLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onReset();
          onCloseModal();
        },
      });
    } else {
      onReset();
      resetAndRefresh();
      onCloseModal();
    }
  };

  const onFinish = () => {
    formRef.validateFields().then((formData) => {
      formData.isAgencyAccount = isAgencyAccount;
      dispatch(actions.saveGlobalTelecomAccount(formData)).then(() => {
        dispatch(actions.getMessageControlParameters()).then(() => {
          modal.success({
            title: 'Your settings are saved successfully',
            onOk: () => {
              setIsModified(false);
              onClose(false);
            },
          });
        });
      });
    });
  };

  const onRemove = () => {
    if (!globalTelecomAccount) {
      return;
    }

    modal.confirm({
      title: 'This is remove the current Global Telecom Account settings. Are you sure you want to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        return new Promise((resolve, reject) => {
          dispatch(
            actions.deleteGlobalTelecomAccount({
              globalTelecomAccountId: globalTelecomAccount.id,
              isAgencyAccount: isAgencyAccount,
            }),
          )
            .then(() => {
              return dispatch(actions.getMessageControlParameters()).then(() => {
                modal.success({
                  title: 'Your settings are saved successfully',
                  onOk: () => {
                    setIsModified(false);
                    onClose(false);
                    resolve(undefined);
                  },
                });
              });
            })
            .catch(reject);
        });
      },
    });
  };

  const onReset = () => {
    formRef.resetFields();
    setIsModified(false);
  };

  const onGlobalAgencysSettingClick = () => {
    setIsAgencyAccount(true);
  };

  return (
    <Modal
      className="account-modal-settings"
      title="Global Telecom account settings"
      open={showModal}
      footer={null}
      onCancel={() => onClose(isModified)}
      onClose={() => onClose(isModified)}
      width={600}
    >
      <Spin spinning={globalTelecomAccountLoading || !initiatingDone}>
        <Form
          form={formRef}
          size="small"
          className="global-telecom-account-form"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          initialValues={globalTelecomAccount as Store}
          onFinish={onFinish}
          onChange={() => (isModified ? null : setIsModified(true))}
        >
          <Form.Item hidden name="id">
            <Input type="hidden" />
          </Form.Item>

          <Form.Item label="API key" name="apiKey" rules={[{ type: 'string', required: true, max: 128 }]}>
            <Input />
          </Form.Item>

          <Form.Item label="API secret" name="apiSecret" rules={[{ type: 'string', required: true, max: 128 }]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Phone number"
            name="phoneNumber"
            rules={[
              {
                type: 'string',
                required: true,
              },
              {
                pattern: new RegExp(/^\+\d{11}$/),
                message: "Invalid phone number format. Please use '+12345678912' format.",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Row>
            <Col span={24} className="modal-form-control-buttons">
              <Space className="control-buttons-space">
                {userInfo?.userType === UserTypes.Agent && !isAgencyAccount ? (
                  <Tooltip title="Switch to the Agency's setting">
                    <Button htmlType="button" onClick={onGlobalAgencysSettingClick}>
                      Global Agency's setting
                    </Button>
                  </Tooltip>
                ) : undefined}

                <Button type="primary" htmlType="submit" loading={saveGlobalTelecomAccountLoading || messageControlParametersLoading}>
                  {!isEmpty(globalTelecomAccount?.id) ? 'Update' : 'Save'}
                </Button>

                <Tooltip title="Reset all changes to the unchanged state">
                  <Button htmlType="button" onClick={onReset}>
                    Reset
                  </Button>
                </Tooltip>

                {!isEmpty(globalTelecomAccount?.id) ? (
                  <Button type="primary" danger loading={saveGlobalTelecomAccountLoading || messageControlParametersLoading} onClick={onRemove}>
                    Remove
                  </Button>
                ) : undefined}

                <Button onClick={() => onClose(isModified)}>Cancel</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default GlobalTelecomAccountComponent;
